import { useMemo, useRef, useState } from 'react';
import ElementFace from '../ElementFace';
import { X } from 'lucide-react';
import {
  CATEGORY_COLOR,
  CATEGORY_LABELS,
  normalizeCategory,
  type ElementCategory,
} from '../elements';
import {
  FULL_ELEMENTS,
  TRENDS,
  heatColor,
  type FullElement,
  type TrendKey,
  type TrendMeta,
} from './atomicData';
import {
  VizCard,
  NowYouTry,
  DARK_INK,
  DARK_INK_SOFT,
  VIZ_FOCUS,
  cueIsActive,
  cueHighlights,
  usePrefersReducedMotion,
  useMountSequence,
  type VizCue,
} from '../vizShared';

// PeriodicTablePro — a college-level interactive periodic table.
//   • category coloring (default) OR a TREND HEAT OVERLAY the user toggles
//     (atomic radius, electronegativity, 1st ionization energy, electron
//     affinity, melting point) with a continuous color scale + legend + blurb.
//   • click an element → rich detail panel (Z, symbol, name, mass, electron
//     configuration + noble-gas shorthand, common oxidation states, category,
//     group/period/block, and all five trend values).
//   • hover tooltip; optional config spotlight (everything else dims).
//   • self-demo on mount: briefly sweeps a trend overlay, then hands control
//     back with a "Now you try" pill. Optional `cue` supersedes the demo and
//     can spotlight symbols / a category / a block, or switch the overlay.
// Resilient to partial/empty config; elements without a curated trend value are
// shown hatched-grey in overlay mode rather than crashing.

export interface PeriodicTableProConfig {
  title?: string;
  // Start in a trend overlay instead of category colors.
  trend?: TrendKey;
  // Spotlight (everything else dims). Same shape as InteractivePeriodicTable.
  highlightCategory?: ElementCategory;
  highlightGroup?: number;
  highlightPeriod?: number;
  highlightSymbols?: string[];
  highlightBlock?: 's' | 'p' | 'd' | 'f';
}

interface Props {
  config?: PeriodicTableProConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

interface Placed extends FullElement {
  col: number;
  row: number;
}

const F_BLOCK_START_COL = 3;

const TREND_KEYS = new Set(TRENDS.map((t) => t.key.toLowerCase()));
const CATEGORY_KEYS = new Set(Object.keys(CATEGORY_COLOR));

/** The range markers that stand in for the f block inside the main grid. The
 *  elements themselves are laid out in the detached rows 9 and 10. */
const F_BLOCK_MARKERS = [
  { row: 6, label: '57-71' },
  { row: 7, label: '89-103' },
];

function placeElements(): Placed[] {
  const placed: Placed[] = [];
  const lanth = FULL_ELEMENTS.filter((e) => e.category === 'lanthanide' && e.group === null).sort((a, b) => a.z - b.z);
  const actin = FULL_ELEMENTS.filter((e) => e.category === 'actinide' && e.group === null).sort((a, b) => a.z - b.z);
  for (const e of FULL_ELEMENTS) {
    if (e.group === null) continue;
    placed.push({ ...e, col: e.group, row: e.period });
  }
  lanth.forEach((e, idx) => placed.push({ ...e, col: F_BLOCK_START_COL + idx, row: 9 }));
  actin.forEach((e, idx) => placed.push({ ...e, col: F_BLOCK_START_COL + idx, row: 10 }));
  return placed;
}

export default function PeriodicTablePro({ config = {}, onComplete, cue }: Props) {
  const placed = useMemo(placeElements, []);
  const reducedMotion = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const [selected, setSelected] = useState<Placed | null>(null);
  const [hovered, setHovered] = useState<Placed | null>(null);
  const [activeTrend, setActiveTrend] = useState<TrendKey | null>(config.trend ?? null);
  const [interacted, setInteracted] = useState(false);

  // Self-demo: briefly sweep a trend overlay, then settle back to category color
  // and show "Now you try". Skipped under reduced motion or when a cue drives.
  const demoTrend: TrendKey = config.trend ?? TRENDS[0]?.key ?? 'electronegativity';
  const demo = useMountSequence(
    [
      { at: 420, run: () => setActiveTrend(demoTrend) },
      { at: 2020, run: () => setActiveTrend(config.trend ?? null) },
    ],
    {
      skip: reducedMotion || hasCue,
      onSettle: () => setActiveTrend(config.trend ?? null),
    },
  );
  const demoRef = useRef(demo);
  demoRef.current = demo;

  // ── Cue handling: spotlight symbols/category/block, optional overlay switch ──
  const cueTokens = useMemo(() => cueHighlights(cue), [cue]);
  const cueSymbols = useMemo(
    () => new Set(cueTokens.filter((t) => /^[a-z]{1,3}$/.test(t))),
    [cueTokens],
  );
  // Resolve each cue token through the same family→category normalizer the config
  // path uses, so a spoken "alkaline earth metals" / "noble gases" spotlights its
  // region (previously only exact category keys matched here, so families never lit).
  const cueCategories = useMemo(
    () =>
      cueTokens
        .map((t) => normalizeCategory(t))
        .filter((t): t is string => !!t && CATEGORY_KEYS.has(t)),
    [cueTokens],
  );
  const cueBlocks = useMemo(
    () => new Set(cueTokens.filter((t) => /^[spdf]$/.test(t))),
    [cueTokens],
  );

  // A cue naming a known trend key switches the overlay (nice-to-have).
  const cueTrend = useMemo<TrendKey | null>(() => {
    const hit = cueTokens.find((t) => TREND_KEYS.has(t));
    return (hit as TrendKey) ?? null;
  }, [cueTokens]);
  // Apply cue overlay once it appears (cancel demo first).
  const lastCueTrend = useRef<TrendKey | null>(null);
  if (cueTrend && cueTrend !== lastCueTrend.current) {
    lastCueTrend.current = cueTrend;
    if (!demo.done) demoRef.current.cancel();
    if (activeTrend !== cueTrend) setActiveTrend(cueTrend);
  }

  const {
    highlightCategory,
    highlightGroup,
    highlightPeriod,
    highlightSymbols,
    highlightBlock,
  } = config;

  const catKey = normalizeCategory(highlightCategory);
  // Only count highlightCategory as a config spotlight when it resolves to a real
  // category — a junk value like "all families" must NOT dim the whole table.
  const catKeyValid = !!catKey && CATEGORY_KEYS.has(catKey);
  const configSpotlight =
    catKeyValid ||
    highlightGroup != null ||
    highlightPeriod != null ||
    highlightBlock != null ||
    (highlightSymbols != null && highlightSymbols.length > 0);

  const cueSpotlight =
    cueSymbols.size > 0 || cueCategories.length > 0 || cueBlocks.size > 0;

  const hasSpotlight = configSpotlight || cueSpotlight;

  const symbolSet = useMemo(
    () => new Set((highlightSymbols ?? []).map((s) => s.toLowerCase())),
    [highlightSymbols],
  );

  const isSpotlit = (e: Placed): boolean => {
    if (!hasSpotlight) return true;
    if (catKeyValid && e.category === catKey) return true;
    if (highlightGroup != null && e.group === highlightGroup) return true;
    if (highlightPeriod != null && e.period === highlightPeriod) return true;
    if (highlightBlock != null && e.block === highlightBlock) return true;
    if (symbolSet.has(e.symbol.toLowerCase())) return true;
    // Cue-driven spotlight.
    if (cueSymbols.has(e.symbol.toLowerCase())) return true;
    if (cueCategories.includes(e.category)) return true;
    if (cueBlocks.has(e.block)) return true;
    return false;
  };

  const trendMeta: TrendMeta | null = useMemo(
    () => (activeTrend ? TRENDS.find((t) => t.key === activeTrend) ?? null : null),
    [activeTrend],
  );

  // Min/max for the active trend, computed across elements that HAVE a value.
  const trendRange = useMemo(() => {
    if (!trendMeta) return null;
    let min = Infinity;
    let max = -Infinity;
    for (const e of placed) {
      const v = trendMeta.accessor(e);
      if (v == null) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    return Number.isFinite(min) ? { min, max } : null;
  }, [trendMeta, placed]);

  const markInteracted = () => {
    if (!interacted) {
      setInteracted(true);
      onComplete?.({ type: 'periodic_table_pro' });
    }
  };

  // Cancel the self-demo on the first user interaction.
  const cancelDemo = () => {
    if (!demoRef.current.done) demoRef.current.cancel();
  };

  const handleSelect = (e: Placed) => {
    cancelDemo();
    setSelected(e);
    markInteracted();
  };

  const toggleTrend = (key: TrendKey) => {
    cancelDemo();
    setActiveTrend((cur) => (cur === key ? null : key));
    markInteracted();
  };

  // Cell fill: category color OR trend heat (hatched grey when no value).
  const cellFill = (e: Placed): { bg: string; faded: boolean } => {
    if (!trendMeta || !trendRange) return { bg: CATEGORY_COLOR[e.category], faded: false };
    const v = trendMeta.accessor(e);
    if (v == null) return { bg: 'var(--ws-surface-2)', faded: true };
    const span = trendRange.max - trendRange.min || 1;
    return { bg: heatColor((v - trendRange.min) / span), faded: false };
  };

  const legendCats = useMemo(() => {
    const set = new Set<ElementCategory>();
    FULL_ELEMENTS.forEach((e) => set.add(e.category));
    return Array.from(set);
  }, []);

  const transition = reducedMotion ? undefined : 'background-color 500ms ease-out, opacity 400ms ease-out';

  return (
    <VizCard
      title={config.title ?? 'Interactive periodic table'}
      eyebrow="Periodic table"
      subtitle="Click an element · toggle a trend overlay"
      hint="Click any element for its full profile · toggle a trend to see the heat map fade in"
      reducedMotion={reducedMotion}
    >
      <div className="relative w-full">
        <NowYouTry show={demo.done && !hasCue && !reducedMotion} />

        {/* Trend toggles */}
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">
            Color by
          </span>
          <button
            type="button"
            onClick={() => {
              cancelDemo();
              setActiveTrend(null);
              markInteracted();
            }}
            className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${VIZ_FOCUS} ${
              activeTrend === null
                ? 'bg-ws-accent text-ws-on-accent'
                : 'bg-ws-surface-2 text-ws-ink-2 hover:bg-ws-surface'
            }`}
          >
            Category
          </button>
          {TRENDS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => toggleTrend(t.key)}
              className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${VIZ_FOCUS} ${
                activeTrend === t.key
                  ? 'bg-ws-accent text-ws-on-accent'
                  : 'bg-ws-surface-2 text-ws-ink-2 hover:bg-ws-surface'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto pb-1" onPointerDown={cancelDemo}>
          <div
            className="grid gap-[2px]"
            style={{
              gridTemplateColumns: 'repeat(18, minmax(1.5rem, 1fr))',
              gridTemplateRows: 'repeat(7, auto) 0.4rem repeat(2, auto)',
              minWidth: '34rem',
            }}
          >
            {/* The "57–71" / "89–103" markers the textbook prints in group 3.
                Lu and Lr used to sit here as real cells (the IUPAC-2021
                placement), which left the detached rows 14 wide, starting at
                Ce and Th. Every table the students hold — including the course
                slide — uses the older convention: a range marker here and all
                FIFTEEN elements below. A teaching table that disagrees with the
                one in front of the class is just a second thing to explain. */}
            {F_BLOCK_MARKERS.map((mk) => (
              <div
                key={mk.label}
                aria-hidden="true"
                style={{ gridColumn: 3, gridRow: mk.row }}
                className="flex items-center justify-center rounded border border-dashed border-ws-ink-4/50 bg-ws-surface-2 text-[8px] font-semibold leading-none text-ws-ink-3"
                title={`${mk.label} are shown in the row below`}
              >
                {mk.label}
              </div>
            ))}
            {placed.map((e) => {
              const lit = isSpotlit(e);
              const { bg, faded } = cellFill(e);
              const isSel = selected?.z === e.z;
              return (
                <button
                  key={e.z}
                  type="button"
                  onClick={() => handleSelect(e)}
                  onMouseEnter={() => setHovered(e)}
                  onMouseLeave={() => setHovered((h) => (h?.z === e.z ? null : h))}
                  title={`${e.name} (${e.symbol})`}
                  style={{
                    gridColumn: e.col,
                    gridRow: e.row,
                    backgroundColor: bg,
                    opacity: lit ? 1 : 0.45,
                    transition,
                    backgroundImage: faded
                      ? 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 6px)'
                      : undefined,
                  }}
                  className={`flex aspect-square min-w-0 flex-col items-center justify-center rounded-[3px] px-0.5 leading-none transition-transform hover:z-10 hover:scale-110 hover:shadow ${VIZ_FOCUS} ${
                    isSel ? 'ring-2 ring-ws-accent' : ''
                  } ${lit ? 'hover:ring-1 hover:ring-ws-ink/40' : ''}`}
                >
                  <span className="text-[6px] font-medium" style={{ color: DARK_INK_SOFT }}>{e.z}</span>
                  <span className="text-[10px] font-bold" style={{ color: DARK_INK }}>{e.symbol}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Legend — category swatches OR trend heat scale */}
        {trendMeta && trendRange ? (
          <div className="mt-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-ws-ink-3">
                {Math.round(trendRange.min)} {trendMeta.unit}
              </span>
              <div
                className="h-2.5 flex-1 rounded-full"
                style={{
                  background: `linear-gradient(to right, ${heatColor(0)}, ${heatColor(0.5)}, ${heatColor(1)})`,
                }}
              />
              <span className="text-[10px] text-ws-ink-3">
                {Math.round(trendRange.max)} {trendMeta.unit}
              </span>
            </div>
            <p className="mt-1.5 text-[11px] text-ws-ink-2">{trendMeta.blurb}</p>
          </div>
        ) : (
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
            {legendCats.map((cat) => (
              <div key={cat} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-[2px]"
                  style={{ backgroundColor: CATEGORY_COLOR[cat] }}
                />
                <span className="text-[10px] text-ws-ink-3">{CATEGORY_LABELS[cat]}</span>
              </div>
            ))}
          </div>
        )}

        {/* Hover tooltip strip (compact, doesn't shift layout) */}
        {hovered && !selected && (
          <div className="mt-2 flex items-baseline gap-2 text-[11px] text-ws-ink-2">
            <span className="font-semibold text-ws-ink">
              {hovered.name} ({hovered.symbol})
            </span>
            <span className="text-ws-ink-3">Z {hovered.z}</span>
            <span className="text-ws-ink-3">·</span>
            <span className="font-mono">{hovered.electronConfig}</span>
            {trendMeta && (
              <>
                <span className="text-ws-ink-3">·</span>
                <span>
                  {trendMeta.label}:{' '}
                  <span className="font-medium text-ws-ink">
                    {trendMeta.accessor(hovered) == null
                      ? 'n/a'
                      : `${Math.round((trendMeta.accessor(hovered) as number) * 100) / 100} ${trendMeta.unit}`}
                  </span>
                </span>
              </>
            )}
          </div>
        )}

        {/* Detail panel */}
        {selected && <DetailPanel e={selected} onClose={() => setSelected(null)} />}
      </div>
    </VizCard>
  );
}

function DetailPanel({ e, onClose }: { e: FullElement; onClose: () => void }) {
  const fmt = (v: number | null, unit: string) =>
    v == null ? '—' : `${v} ${unit}`;
  const ox =
    e.oxidationStates.length === 0
      ? '—'
      : e.oxidationStates
          .map((s) => (s > 0 ? `+${s}` : `${s}`))
          .join(', ');

  return (
    <div className="mt-3 flex items-start gap-3 rounded-md border border-ws-border bg-ws-surface p-3 shadow-sm">
      {/* Shared with InteractivePeriodicTable so the two tables cannot drift
          apart again — same tile size, same legible Z and mass, same
          pronunciation control. */}
      <ElementFace
        z={e.z}
        symbol={e.symbol}
        name={e.name}
        mass={e.mass}
        background={CATEGORY_COLOR[e.category]}
        ink={DARK_INK}
        inkSoft={DARK_INK_SOFT}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <h4 className="text-base font-semibold text-ws-ink">{e.name}</h4>
            <span className="rounded bg-ws-surface-2 px-1.5 py-0.5 text-[10px] font-medium uppercase text-ws-ink-3">
              {e.block}-block
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-sm p-1 text-ws-ink-3 transition-colors hover:bg-ws-surface-2 hover:text-ws-ink ${VIZ_FOCUS}`}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <p className="mt-1 font-mono text-xs text-ws-ink-2">
          {e.shorthand}
        </p>

        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-ws-ink-2 sm:grid-cols-3">
          <Item label="Category" value={CATEGORY_LABELS[e.category]} />
          <Item label="Group" value={e.group == null ? '—' : String(e.group)} />
          <Item label="Period" value={String(e.period)} />
          <Item label="Oxid. states" value={ox} />
          <Item label="Electronegativity" value={e.electronegativity == null ? '—' : e.electronegativity.toFixed(2)} />
          <Item label="Atomic radius" value={fmt(e.atomicRadius, 'pm')} />
          <Item label="1st ionization" value={fmt(e.ionizationEnergy, 'kJ/mol')} />
          <Item label="Electron affinity" value={fmt(e.electronAffinity, 'kJ/mol')} />
          <Item label="Melting point" value={fmt(e.meltingPoint, 'K')} />
        </dl>
      </div>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="inline text-ws-ink-3">{label}: </dt>
      <dd className="inline font-medium">{value}</dd>
    </div>
  );
}
